import { ref } from 'vue'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'

export interface FkCounts {
  transactions: number
  service_calls: number
  lease_contracts: number
  part_options: number
  transaction_items: number
}

export interface PartnerSummary {
  id: string
  name: string | null
  legal_name: string | null
  invoicing_name: string | null
  type: string | null
  status: string | null
  default_email: string | null
  default_phone: string | null
  billing_address: unknown
  shipping_address: unknown
  created_at: string | null
  updated_at: string | null
  last_transaction_at: string | null
  company_id: string | null
  company_name: string | null
  fk_counts: FkCounts
}

export interface DuplicateCluster {
  keeper: PartnerSummary
  losers: PartnerSummary[]
  pair_count: number
  max_score: number
  min_score: number
  transaction_types: Record<string, number>
  suggest_supplier: boolean
  keeper_type: string | null
}

export interface MergeConflict {
  key: string
  table: string
  label: string
  keeper: Record<string, unknown>
  loser: Record<string, unknown>
}

export interface MergeDrop {
  table: string
  reason: string
  label: string
  value: Record<string, unknown>
}

export interface MergePreview {
  conflicts: MergeConflict[]
  drops: MergeDrop[]
}

interface ClustersResponse {
  data: DuplicateCluster[]
  meta?: {
    total_clusters?: number
    obvious_count?: number
  }
}

export function usePartnerDedup() {
  const clusters = ref<DuplicateCluster[]>([])
  const totalClusters = ref(0)
  const obviousCount = ref(0)
  const loading = ref(false)
  const scanning = ref(false)
  const autoMerging = ref(false)
  const error = ref<string | null>(null)

  async function load(opts: { search?: string } = {}) {
    loading.value = true
    error.value = null
    const params: Record<string, string> = {}
    if (opts.search) params.search = opts.search
    try {
      const response = await axiosApi.get<ClustersResponse>('/business-partners/duplicates/clusters', { params })
      clusters.value = response.data.data
      totalClusters.value = response.data.meta?.total_clusters ?? response.data.data.length
      obviousCount.value = response.data.meta?.obvious_count ?? 0
    } catch (e) {
      error.value = (e as Error).message
    } finally {
      loading.value = false
    }
  }

  async function scan(threshold?: number) {
    scanning.value = true
    error.value = null
    try {
      await axiosApi.post('/business-partners/duplicates/scan', { threshold })
    } catch (e) {
      error.value = (e as Error).message
    } finally {
      scanning.value = false
    }
  }

  async function autoMerge(): Promise<{ merged: number; skipped: number } | null> {
    autoMerging.value = true
    error.value = null
    try {
      const response = await axiosApi.post<{ merged: number; skipped: number }>('/business-partners/duplicates/auto-merge')
      return response.data
    } catch (e) {
      error.value = (e as Error).message
      return null
    } finally {
      autoMerging.value = false
    }
  }

  async function previewMerge(keeperId: string, loserId: string): Promise<MergePreview> {
    const r = await axiosApi.get<MergePreview>(`/business-partners/${keeperId}/merge/${loserId}/preview`)
    return r.data
  }

  async function mergeOne(
    keeperId: string,
    loserId: string,
    resolutions: Record<string, 'keeper' | 'loser'> = {},
  ): Promise<void> {
    error.value = null
    try {
      await axiosApi.post(`/business-partners/${keeperId}/merge/${loserId}`, { resolutions })
    } catch (e) {
      error.value = (e as Error).message
      throw e
    }
  }

  async function mergeCluster(
    keeperId: string,
    loserIds: string[],
    resolutions: Record<string, 'keeper' | 'loser'> = {},
    fieldWinners: Record<string, string> = {},
    convertToSupplier = false,
  ): Promise<{ merged: number; failed: string[]; type_changed?: boolean } | null> {
    error.value = null
    try {
      const r = await axiosApi.post<{ merged: number; failed: string[]; type_changed?: boolean }>(
        '/business-partners/duplicates/cluster-merge',
        {
          keeper_id: keeperId,
          loser_ids: loserIds,
          resolutions,
          field_winners: fieldWinners,
          convert_to_supplier: convertToSupplier,
        },
      )
      return r.data
    } catch (e) {
      error.value = (e as Error).message
      throw e
    }
  }

  return {
    clusters,
    totalClusters,
    obviousCount,
    loading,
    scanning,
    autoMerging,
    error,
    load,
    scan,
    autoMerge,
    previewMerge,
    mergeOne,
    mergeCluster,
  }
}
